// React Hooks:
import React, { useState, useEffect } from "react";
// Redux Toolkit Hooks:
import { useAppSelector, useAppDispatch } from "../../../../../app/hooks";
// Redux Toolkit Actions & State:
import {
  uploadFileAction,
  uploadFileCloseAction,
  uploadFile,
  cancelUploadFileAction,
} from "./uploadFileSlice";
import { directory, searchInputAction } from "../../directory/directorySlice";
// MUI Components:
import {
  Modal,
  Container,
  Box,
  Typography,
  useTheme,
  Button,
  SvgIcon,
  LinearProgress,
  Fade,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "notistack";
// MUI Styles:,
import { makeStyles } from "@mui/styles";
// Icones:
import { ReactComponent as DragAnDropIcon } from "../../../../../assets/icons/dragAnDrop-icon.svg";
import { ReactComponent as CircleCheckIcon } from "../../../../../assets/icons/circleCheck-icon.svg";
import { ReactComponent as CircleRemoveIcon } from "../../../../../assets/icons/circleRemove-icon.svg";
import CloseIcon from "@mui/icons-material/Close";


// Type Controller
interface InputDataState {
  file: any;
  available: boolean;
}

const useStyles = makeStyles((theme) => ({
  backdropStyle: {
    "& .css-i9fmh8-MuiBackdrop-root-MuiModal-backdrop": {
      backdropFilter: "blur(2px)",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  },
  modalContainer: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    borderRadius: "8px",
    "&:focus": {
      outline: "none",
    },
  },
  images: {
    width: "auto",
    height: "60px",
    color: theme.palette.secondary.light,
    objectFit: "cover",
    [theme.breakpoints.down("md")]: {
      height: "55px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "50px",
    },
  },
}));

const UploadFile = () => {
  const mime = require("mime");

  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const {
    openModal,
    uploadFileData: { actionStatus, alert: { message } },
  } = useAppSelector(uploadFile);
  const {
    directoryData: { items, path, idfolder },
  } = useAppSelector(directory);

  const [inputData, setInputData] = useState<InputDataState>({
    file: null,
    available: false,
  });
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [onlineRetrying, setOnlineRetrying] = useState(false);

  useEffect(() => {
    if (actionStatus === "success") {
      setUploadPercentage(100);
    }
  }, [actionStatus, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!navigator.onLine) {
        dispatch(cancelUploadFileAction("Your system is offlined."));

        setOnlineRetrying(true);
      } else if (navigator.onLine && onlineRetrying === true) {
        setOnlineRetrying(false);

        const formData = new FormData();
        formData.append("file", inputData.file, inputData.file.name);
        formData.append("path", path);
        formData.append("idfolder", idfolder);

        dispatch(searchInputAction(""));
        dispatch(uploadFileAction({ formData, setUploadPercentage }));

        enqueueSnackbar("Congratulations, you are online now. Reupload is in progress", { variant: "success" })
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch, idfolder, inputData.file, onlineRetrying, path]);

  useEffect(() => {
    if (message === "Your system is offlined.") {
      enqueueSnackbar("Your internet is currently down. Check and try again", { variant: "error" })
    }
    if (message === "Network Error") {
      enqueueSnackbar("Network error. try again later", { variant: "error" })
      dispatch(uploadFileCloseAction())
      dispatch(cancelUploadFileAction(
        "The file upload was canceled by the client."
      ))
    }
  }, [message])

  const handleDragOver = (e: any) => {
    e.preventDefault();
  };

  const handleDrop = (e: any) => {
    if (
      !!items.find((item) => item.name === e.dataTransfer.files[0].name) ===
      false
    ) {
      e.preventDefault();
      const formData = new FormData();
      formData.append(
        "file",
        e.dataTransfer.files[0],
        e.dataTransfer.files[0].name
      );
      formData.append("path", path);
      formData.append("idfolder", idfolder);

      dispatch(searchInputAction(""));
      dispatch(uploadFileAction({ formData, setUploadPercentage }));

      setInputData((last) => ({
        ...last,
        file: e.dataTransfer.files[0],
        available: !!e.dataTransfer.files[0]?.type ? true : true,
      }));
    } else {
      enqueueSnackbar("You have already created a file with the same name!", {
        variant: "error",
      });
    }
  };

  const handleFileChange = (e: any) => {
    if (
      !!items.find((item) => item.name === e.target.files[0].name) === false
    ) {
      const formData = new FormData();
      formData.append("file", e.target.files[0], e.target.files[0].name);
      formData.append("path", path);
      formData.append("idfolder", idfolder);

      dispatch(searchInputAction(""));
      dispatch(uploadFileAction({ formData, setUploadPercentage }));

      setInputData((last) => ({
        ...last,
        file: e.target.files[0],
        available: !!e.target.files[0]?.type ? true : true,
      }));
    } else {
      enqueueSnackbar("You have already created a file with the same name!", {
        variant: "error",
      });
    }
  };

  const setImageSrc = (type: string) => {
    const checkImageExists = (type: string) => {
      try {
        require(`../../../../../assets/megaDL-icones/${type}.png`);
        return true;
      } catch (error) {
        return false;
      }
    };

    if (!!type && checkImageExists(type)) {
      return require(`../../../../../assets/megaDL-icones/${type}.png`);
    } else {
      return require(`../../../../../assets/megaDL-icones/none.png`);
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={() =>
        inputData.available === false && dispatch(uploadFileCloseAction())
      }
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      container={() => document.getElementById("modalContainer")}
      sx={{
        width: "100%",
        height: "100%",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 2,
        "&:focus": {
          outline: "none",
        },
      }}
      className={classes.backdropStyle}
      id="ignoreDisableTouch"
    >
      <Container
        maxWidth={false}
        sx={{
          width: "645px",
          height:
            uploadPercentage === 100
              ? "303px"
              : inputData.available
                ? "238px"
                : "418px",
          minHeight: "230px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 12,
          p: { xs: 3 },
          transition: "all 0.5s",
          "&:focus-visible": {
            outline: "none",
          },
          [theme.breakpoints.down("md")]: {
            width: "85%",
            height: "fit-content",
            minHeight: "fit-content",
            transition: "all 0.4s",
          },
        }}
        className={classes.modalContainer}
      >
        <IconButton onClick={() => {
          dispatch(uploadFileCloseAction())
          dispatch(cancelUploadFileAction(
            "The file upload was canceled by the user."
          ))
        }} sx={{ position: "absolute", right: "10px", top: "10px" }}>
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            mb: "24px",
            [theme.breakpoints.down("sm")]: {
              mb: "16px",
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "26px",
              mb: "8px",
              fontWeight: "bolder",
              color: theme.palette.secondary.main,
            }}
          >
            Upload File
          </Typography>

          <Typography
            sx={{
              fontSize: "16px",
              color: "inherit",
              opacity: "0.5",
            }}
          >
            Upload Documents You Want To Share With Your Team
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: inputData.available ? "95px" : "275px",
            border: inputData.available
              ? `2px solid ${theme.palette.secondary.main}`
              : `2px dashed ${theme.palette.secondary.main}`,
            backgroundColor: "#FBFAFE",
            borderRadius: "16px",
            textAlign: "center",
            transition: "all 0.5s",
            [theme.breakpoints.down("sm")]: {
              width: "100%",
              height: "fit-content",
              border: inputData.available
                ? `2px solid ${theme.palette.secondary.main}`
                : "none",
              backgroundColor: "transparent",
            },
          }}
        >
          {inputData.available === false && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "transparent",
                borderRadius: "8px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-evenly",
                [theme.breakpoints.down("sm")]: {
                  display: "none",
                },
              }}
              onDragOver={(e: any) => handleDragOver(e)}
              onDrop={(e: any) => handleDrop(e)}
            >
              <SvgIcon
                component={DragAnDropIcon}
                inheritViewBox
                sx={{
                  width: "55px",
                  height: "55px",
                  color: "inherit",
                }}
              />

              <Typography
                sx={{
                  width: "200px",
                  fontSize: "16px",
                  color: "inherit",
                  opacity: "0.5",
                  lineHeight: "2",
                }}
              >
                Drag & Drop your files here OR
              </Typography>

              <Button
                variant="contained"
                color="blue"
                component="label"
                fullWidth
                sx={{
                  width: "165px",
                  height: "45px",
                  fontSize: "18px",
                  borderRadius: "8px",
                  textTransform: "none",
                  lineHeight: "0px",
                  padding: "12px",
                }}
              >
                Browse File
                <input
                  hidden
                  // accept="image/*"
                  type="file"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>
          )}

          {inputData.available === false && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "transparent",
                borderRadius: "8px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "space-evenly",
                [theme.breakpoints.up("sm")]: {
                  display: "none",
                },
              }}
            >
              <Button
                variant="contained"
                color="blue"
                component="label"
                fullWidth
                sx={{
                  width: "165px",
                  height: "45px",
                  fontSize: "18px",
                  borderRadius: "8px",
                  textTransform: "none",
                  lineHeight: "0px",
                  padding: "12px",
                }}
              >
                Browse File
                <input
                  hidden
                  // accept="image/*"
                  type="file"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>
          )}

          {inputData.available === true && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: "95px",
                backgroundColor: "transparent",
                borderRadius: "8px",
                textAlign: "center",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-evenly",
              }}
            >
              <Box
                component="img"
                src={setImageSrc(mime.getExtension(inputData.file?.type))}
                alt="images"
                className={classes.images}
              />

              <Box
                sx={{
                  width: "420px",
                  [theme.breakpoints.down("md")]: {
                    width: "80%",
                  },
                  [theme.breakpoints.down("sm")]: {
                    width: "70%",
                  },
                  [theme.breakpoints.down(460)]: {
                    width: "60%",
                  },
                  [theme.breakpoints.down(360)]: {
                    width: "55%",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "inherit",
                      opacity: "0.5",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      width: "85%",
                      height: "24px",
                      textAlign: "left",
                      [theme.breakpoints.down("sm")]: {
                        width: "75%",
                      },
                      [theme.breakpoints.down(480)]: {
                        width: "65%",
                      },
                      [theme.breakpoints.down(360)]: {
                        width: "50%",
                      },
                    }}
                  >
                    {inputData.file?.name}
                  </Typography>

                  <Typography
                    sx={{
                      height: "24px",
                      fontSize: "16px",
                      color: "inherit",
                      opacity: "0.5",
                    }}
                  >
                    {`${uploadPercentage} %`}
                  </Typography>
                </Box>

                <LinearProgress
                  color="secondary"
                  variant="determinate"
                  value={uploadPercentage}
                  sx={{ height: "10px", borderRadius: "8px" }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-around",
                  pt: "25px",
                }}
              >
                {uploadPercentage === 100 && (
                  <Box>
                    <Fade in={uploadPercentage === 100} timeout={1200}>
                      <SvgIcon
                        component={CircleCheckIcon}
                        inheritViewBox
                        sx={{
                          width: "24px",
                          height: "24px",
                          color: theme.palette.success.main,
                        }}
                      />
                    </Fade>
                  </Box>
                )}

                {uploadPercentage !== 100 && (
                  <Box>
                    <Fade in={uploadPercentage !== 100} timeout={1000}>
                      <SvgIcon
                        component={CircleRemoveIcon}
                        inheritViewBox
                        sx={{
                          width: "24px",
                          height: "24px",
                          color: theme.palette.secondary.main,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          dispatch(
                            cancelUploadFileAction(
                              "The file upload was canceled by the user."
                            )
                          );
                          setInputData((last) => ({
                            ...last,
                            file: null,
                            available: false,
                          }));
                          setUploadPercentage(0);
                        }}
                      />
                    </Fade>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {uploadPercentage === 100 && (
          <Box
            sx={{
              width: "100%",
              height: uploadPercentage === 100 ? "fit-content" : "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "20px",
            }}
          >
            <Fade
              in={uploadPercentage === 100 && inputData.available === true}
              timeout={1000}
            >
              <Box>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  type="submit"
                  sx={{
                    width: "180px",
                    height: "45px",
                    fontSize: "18px",
                    borderRadius: "8px",
                    textTransform: "none",
                    lineHeight: "0px",
                    padding: "12px",
                  }}
                  onClick={() => {
                    setInputData({
                      file: null,
                      available: false,
                    });
                    setUploadPercentage(0);
                    dispatch(uploadFileCloseAction());
                  }}
                >
                  Upload Done
                </Button>
              </Box>
            </Fade>
          </Box>
        )}
      </Container>
    </Modal>
  );
};

export default UploadFile;
